"use client";

import styles from "@/styles/product-detail/product-shpping.module.scss";
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Dialog from "@/components/global/Dialog";
import { toast } from "@/components/global/Toast";
import NotifyDialog from "@/components/product-detail/NotifyDialog";
import WholesaleDialog from "@/components/product-detail/WholesaleDialog";
import ExpediteDialog from "@/components/global/ExpediteDialog";
import { Input, Button } from "react-vant";
import productApi from "@/service/modules/productApi";
import { setGtmDataLayer } from "@/utils/gtmDataLayer";

import {
  ProductType,
  PostageDataType,
  paymentMethodItem,
  SpecOptionType,
  BreadCrumbItem,
} from "@/types/product";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomerInfo } from "@/store/slices/userSlice";
import useCartActions from "@/utils/addTocartAction";
import dayjs from "dayjs";

interface Props {
  productDetailData: ProductType;
  paymentMethod: paymentMethodItem[];
  breadCrumb: BreadCrumbItem[];
}
export default function ProductShpping({
  productDetailData,
  paymentMethod,
  breadCrumb,
}: Props) {
  const dispatch = useAppDispatch();
  const { addToCart } = useCartActions();
  // 从Redux store获取用户状态
  const { customerInfo, hasLogin } = useAppSelector((state) => state.user);
  // 组件挂载时获取用户信息
  useEffect(() => {
    dispatch(fetchCustomerInfo());
  }, [dispatch]);

  const [quantity, setQuantity] = useState(1);
  const [showAfterPayDialog, setShowAfterPayDialog] = useState(false);
  const [showZippayDialog, setShowZippayDialog] = useState(false);
  const [showCombineModal, setShowCombineModal] = useState(false);

  const [multipleSpecList, setMultipleSpecList] = useState<SpecOptionType[]>(
    []
  );

  const [postCode, setPostcode] = useState<number | string>("");
  const [suburb, setSuburb] = useState("");
  const [suburbList, setSuburbList]: any = useState([]);

  const initialPostageData: PostageDataType = {
    courier: "",
    courierID: "",
    dTime: "",
    itemError: "",
    postage: "",
    productID: "",
    quantity: 1,
  };
  const [postageData, setPostageData] =
    useState<PostageDataType>(initialPostageData);

  const [postageLoading, setPostageLoading] = useState(false);
  const [postcodeNotAvailable, setPostcodeNotAvailable] = useState(false);
  const [shippingError, setShippingError] = useState(false);
  const [shippingDistanceError, setShippingDistanceError] = useState(false);

  // 加入wishlist
  const [isWish, setIsWish] = useState(false);
  const [wpid, setWpid] = useState(0);
  const [addWishlistLoading, setAddWishlistLoading] = useState(false);
  const [showWishLoginModal, setShowWishLoginModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showWholesaleModal, setShowWholesaleModal] = useState(false);

  // 设置谷歌GTM dataLayer
  useEffect(() => {
    function handleWindowLoad() {
      let ecomm_catname = "";
      if (breadCrumb && breadCrumb.length > 0) {
        ecomm_catname = breadCrumb.map((v: any) => v.name).join(" > ");
      }

    const g_params = {
      "event": "productDetailPage",
      "remarketing_params": {
        "ecomm_prodid": productDetailData?.productID,
        "ecomm_pagetype": "product",
        "ecomm_totalvalue": productDetailData?.price,
        "ecomm_prodname": productDetailData?.name,
        "ecomm_catname": ecomm_catname,
        "ecomm_email": "",
        "ecomm_sku": productDetailData?.productCode
      },
      "params": {
        "userId": customerInfo.customerID,
        "categoryId": null,
        "productId": productDetailData?.productID
      }
    }

      setGtmDataLayer(g_params);
    }

    window.addEventListener("load", handleWindowLoad);

    return () => {
      window.removeEventListener("load", handleWindowLoad);
    };
  }, []);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newQuantity = parseInt(e.target.value, 10);
    setQuantity(newQuantity);
  };

  useEffect(() => {
    if (quantity) {
      calculatePostage();
    }
  }, [quantity]);

  useEffect(() => {
    // 拿到默认postcode
    getDefaultPostcode();
  }, []);

  const getDefaultPostcode = async () => {
    try {
      const res = await productApi.getDefaultPostcode();
      const { postCode } = res.data;
      setPostcode(postCode);
    } catch {
      // 使用兜底postcode、 redux储存优先、再没有使用默认2000
      setPostcode(2000);
    }
  };

  useEffect(() => {
    if (postCode && `${postCode}`.length == 4) {
      getSubUrb();
    } else {
      setSuburb("");
      setSuburbList([]);
    }
  }, [postCode]);

  const getSubUrb = async () => {
    try {
      const res = await productApi.getSuburbByPostcode({ postCode: postCode });
      const { suburbID, suburbs } = res.data;
      if (suburbs && suburbs.length) {
        setSuburb(suburbID || suburbs[0].subID);
        setSuburbList(suburbs);
        setPostcodeNotAvailable(false);
      } else {
        toast.show("Invalid Postcode!");
        setPostcodeNotAvailable(true);
        setSuburb("");
        setSuburbList([]);
        setPostageData(initialPostageData);
      }
    } catch (error: any) {
      toast.show(error.message);
    }
  };

  useEffect(() => {
    if (suburb && `${postCode}`.length == 4) {
      // 获取运费数据
      calculatePostage();
    }
  }, [suburb]);

  // 获取邮费数据
  const calculatePostage = async () => {
    // postCode长度不为4，不请求
    if (String(postCode).length != 4) return;

    // 没有选中currentSuburb，不请求
    if (!suburb) return;

    try {
      setPostageLoading(true);

      let suburbName = "";
      const target = suburbList.filter((item: any) => item.subID == suburb);
      if (target.length) {
        suburbName = target[0].subName;
      }

      const dataObj = {
        postCode: postCode,
        suburb: suburbName,
        suburbID: suburb,
        productID: productDetailData?.productID,
        quantity,
      };

      const res = await productApi.getShippingData(dataObj);
      setPostageLoading(false);
      setShippingDistanceError(false);
      setShippingError(false);
      if (res.data) {
        setPostageData(res.data);
      }
    } catch (error: any) {
      if (error.code == 150002) {
        setShippingDistanceError(true);
        setShippingError(false);
      } else {
        setShippingDistanceError(false);
        setShippingError(true);
      }
      setPostageLoading(false);
      setPostageData(initialPostageData);
    }
  };

  const handleShowCombineModal = () => {
    setShowCombineModal(true);
  };

  // 多规格属性 配置初始化
  useEffect(() => {
    if (productDetailData?.variants && productDetailData?.variants.options) {
      // 提取所有选项到一个数组
      const optionsArray: SpecOptionType[] = [];

      if (productDetailData?.variants.options) {
        const optionKeys = Object.keys(productDetailData.variants.options);

        optionKeys.forEach((key) => {
          if (productDetailData.variants.options[key].length) {
            optionsArray.push({
              name: key,
              defaultVariant: 0,
              optionID: productDetailData.variants.options[key][0].optionID,
              options: productDetailData.variants.options[key],
            });
          }
        });
      }

      // 选中variants.products里面产品ID相同的options，赋值。没得赋值就添加一个选项：Please Select
      const target = productDetailData?.variants.products.filter(
        (item: any) => item.productID == productDetailData.productID
      );
      if (target.length && target[0].options) {
        target[0].options.forEach((option: any) => {
          if (optionsArray && optionsArray.length) {
            optionsArray.forEach((spec: SpecOptionType) => {
              if (option.optionID == spec.optionID) {
                spec.defaultVariant = option.variantID;
              }
            });
          }
        });
      } else if (
        target.length &&
        (!target[0].options || target[0].options.length <= 0)
      ) {
        // 如果没有默认值（数据原因），则加上Please Select选项
        optionsArray.forEach((spec: SpecOptionType) => {
          spec.options?.unshift({
            optionValue: "Please Select",
            additionalValue: "#008000",
            optionID: spec.optionID,
            variantID: 0,
          });
        });
      }

      setMultipleSpecList(optionsArray);
    }
  }, []);

  // 触发更新Spec，跳转到对应的链接
  const handleChangeSpec = (optionID: any, value: number) => {
    // 先复制一份商品列表出来，格式化为前端容易做匹配的格式
    const specProduct = JSON.parse(
      JSON.stringify(productDetailData?.variants.products)
    );

    // 遍历当前选中值的集合，方便后面做匹配
    const selectedOptions = multipleSpecList.map((item) => {
      return {
        optionID: item.optionID,
        variantID: item.defaultVariant,
      };
    });

    // 把刚刚选中的值，更新到selectedOptions
    selectedOptions.forEach((item) => {
      if (item.optionID == optionID) {
        item.variantID = value;
      }
    });

    // 存储匹配的结果 productUrl
    let matchingProductUrl = null;

    // 遍历产品数据，有结果就赋值给matchingProductUrl
    for (const product of specProduct) {
      const productOptions = product.options;

      // 检查 productOptions 是否为空
      if (!Array.isArray(productOptions) || productOptions.length === 0) {
        continue; // 如果 productOptions 为空，跳过该产品
      }

      // 检查当前产品的选项是否与选中值匹配
      if (
        selectedOptions.every((selectedOption) =>
          productOptions.some(
            (productOption: any) =>
              productOption.optionID === selectedOption.optionID &&
              productOption.variantID === selectedOption.variantID
          )
        )
      ) {
        matchingProductUrl = product.url;
        break; // 找到匹配后，立即停止遍历
      }
    }

    // 跳转到选中的URL
    if (matchingProductUrl) {
      window.location.href = matchingProductUrl;
    }
  };

  // 商品销售状态管理，return对应的操作按钮
  const StockStatusBtn = () => {
    if (productDetailData?.preSales) {
      return (
        // 预售按钮
        <Button
          round
          type="primary"
          style={{ fontWeight: "700" }}
          block
          className={styles["pre-sales-btn"]}
          onClick={preOrderNow}
        >
          Pre Sales
        </Button>
      );
    } else {
      if (productDetailData?.inStock && productDetailData?.enabled) {
        // 正常可购买
        return (
          <Button
            round
            type="primary"
            style={{ fontWeight: "700" }}
            block
            className={styles["add-cart"]}
            onClick={(event) => {
              addToCart(
                event,
                {
                  productID: productDetailData.productID,
                  qty: quantity,
                },
                (res: any) => {
                  addToCartCallBack(res);
                }
              );
            }}
          >
            Buy Now
          </Button>
        );
      } else if (
        !productDetailData?.enabled &&
        productDetailData?.discontinued
      ) {
        return (
          <Button
            round
            type="primary"
            style={{ fontWeight: "700" }}
            block
            className={styles["add-cart"]}
          >
            Unavailable
          </Button>
        );
      } else {
        return (
          <Button
            round
            type="primary"
            style={{ fontWeight: "700" }}
            block
            className={styles["add-cart"]}
            onClick={() => setShowNotifyModal(true)}
          >
            Notify Me
          </Button>
        );
      }
    }
  };

  // PRE-ORDER NOW
  function preOrderNow() {
    const href = `/checkout?p=${productDetailData?.productID}&t=${quantity}`;
    const expressHref = `/checkout/express?p=${productDetailData?.productID}&t=${quantity}`;

    if (hasLogin) {
      window.location.href = href;
    } else {
      window.location.href = expressHref;
    }
  }

  const addToCartCallBack = (response: any) => {
    if (response != "error") {
      window.location.href = "/view-cart";
    }
  };

  // 获取是否有加入wishlist
  useEffect(() => {
    const getWishData = async () => {
      try {
        const res = await productApi.getIsWishByProduct(
          productDetailData?.productID
        );
        if (res.data) {
          setIsWish(!!res.data.isWish);

          // 保存下商品的心愿单id。后续remove操作用得上
          if (res.data.isWish) setWpid(res.data.wpid);
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (hasLogin) {
      getWishData();
    }
  }, [hasLogin]);

  // 加入/移除 wishlist
  const handleToggleWish = async () => {
    if (isWish) {
      try {
        if (!wpid) {
          toast.show("System Error");
          return;
        }

        setAddWishlistLoading(true);
        const res = await productApi.removeWishList(wpid);
        if (res.data) {
          toast.show("Removed successfully.");
          setIsWish(false);
          setWpid(0);
          setAddWishlistLoading(false);
        }
      } catch (error: any) {
        toast.show(error.message);
        setAddWishlistLoading(false);
      }
    } else {
      try {
        if (!hasLogin) {
          // 未登录状态，不允许请求加入心愿单。弹起登录弹窗
          setShowWishLoginModal(true);
          return;
        }

        setAddWishlistLoading(true);
        const res = await productApi.addWishList({
          productID: productDetailData?.productID,
        });
        if (res.data) {
          setIsWish(true);
          setWpid(res.data.wishProductID);
          toast.show("Successfully!");
          setAddWishlistLoading(false);
        }
      } catch (error: any) {
        toast.show(error.message);
        setAddWishlistLoading(false);
      }
    }
  };

  const handleLogin = () => {
    const currentPath = window.location.pathname + window.location.search;
    const encodedRedirect = encodeURIComponent(currentPath);

    window.location.href = `/login?redirect=${encodedRedirect}`;
  };

  return (
    <div className={styles["comp-product-shpping"]}>
      {hasLogin ? <ExpediteDialog></ExpediteDialog> : null}
      <div className={styles["afterpay-benefit"]}>
        4 installments with
        <Image
          className={styles["afterpay-icon"]}
          style={{ marginTop: "2px" }}
          src={`${process.env.IMAGE_DOMAIN}/product-detail/icon-afterpay.png`}
          alt="afterpay"
          width={69}
          height={13}
          onClick={() => setShowAfterPayDialog(true)}
        />
        or
        <Image
          className={styles["afterpay-icon"]}
          src={`${process.env.IMAGE_DOMAIN}/product-detail/icon-zippay.png`}
          alt="zippay"
          width={36}
          height={13}
          onClick={() => setShowZippayDialog(true)}
        />
      </div>

      <div className={styles["quantity-box"]}>
        <div className={styles["quantity-title"]}>Quantity:</div>

        <select
          value={quantity}
          onChange={handleQuantityChange}
          className={styles["quantity-select"]}
          disabled={
            !productDetailData.enabled || !productDetailData.inStock
              ? true
              : false
          }
        >
          {[
            ...Array.from(
              {
                length:
                  productDetailData.inStock >= 50
                    ? 50
                    : Math.max(1, productDetailData.inStock || 1),
              },
              (_, i) => i + 1
            ).map((num) => (
              <option key={num} value={num}>
                {num == 50 ? "50(max)" : num}
              </option>
            )),
          ]}
        </select>

        <div className={styles["status-box"]}>
          <div
            className={`
              ${styles["status-sign"]} 
              ${
                !productDetailData?.preSales &&
                productDetailData?.inStock &&
                productDetailData?.inStock <= 5
                  ? styles["sign-almost-sold-out"]
                  : ""
              }
              ${
                !productDetailData?.preSales && !productDetailData?.inStock
                  ? styles["sign-sold-out"]
                  : ""
              }
            `}
          ></div>
          {!!productDetailData?.preSales && (
            <div className={styles["status-text"]}>Pre-order </div>
          )}
          {!!(
            !productDetailData?.preSales &&
            productDetailData?.inStock &&
            productDetailData.inStock > 5
          ) && <div className={styles["status-text"]}>In Stock</div>}
          {!!(
            !productDetailData?.preSales &&
            productDetailData?.inStock &&
            productDetailData.inStock <= 5
          ) && <div className={styles["status-text"]}>Almost Sold out</div>}
          {!!(!productDetailData?.preSales && !productDetailData?.inStock) && (
            <div
              className={`${styles["status-text"]} ${styles["status-text-sold-out"]}`}
            >
              Sold Out
            </div>
          )}
        </div>
      </div>

      {/* 多规格属性 */}
      {!!(multipleSpecList && multipleSpecList.length) && (
        <div className={styles["multiple-spec-box"]}>
          {multipleSpecList.map((item: SpecOptionType, index: number) => (
            <div className={styles["item"]} key={`multiple-${index}`}>
              <div className={styles["label"]}>
                {item.name.charAt(0).toUpperCase() + item.name.slice(1)}:
              </div>
              <select
                value={Number(item.defaultVariant)}
                style={{ width: "60%" }}
                onChange={(e) => {
                  handleChangeSpec(item.optionID, Number(e.target.value));
                }}
              >
                {item.options?.map((option: any) => (
                  <option key={option.variantID} value={option.variantID}>
                    {option.optionValue}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      <div className={styles["shipping-box"]}>
        <div className={styles["suburb-select"]}>
          <div className={styles["suburb-select-box"]}>
            PostCode:
            <Input
              className={styles["post-code-input"]}
              value={String(postCode)}
              onChange={(text) => {
                const value = String(text)
                  .replace(/[^0-9]/g, "")
                  .slice(0, 4);
                setPostcode(value);
              }}
              type="number"
              maxLength={4}
            ></Input>
            <select
              className={styles["suburb-select-dropdown"]}
              value={suburb}
              onChange={(e) => {
                const selectedSuburbID = e.target.value;
                setSuburb(selectedSuburbID);
              }}
            >
              {suburbList.map((item: any) => (
                <option key={item.subID} value={item.subID}>
                  {item.subName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles["postage-box"]}>
          {!!postcodeNotAvailable && (
            <div className={styles["error-message"]}>
              Postcode Not Available
            </div>
          )}
          {!!shippingError && (
            <div className={styles["error-message"]}>Not available.</div>
          )}
          {!!(shippingDistanceError && suburbList.length) && (
            <div className={styles["error-message"]}>Non-delivery areas. </div>
          )}

          <div className={styles["postage-item"]}>
            <div className={styles["title"]}>Delivery & Handling: </div>
            <div className={styles["value"]}>
              {!!postageLoading && "Calculating..."}
              {!!(
                !postageLoading &&
                productDetailData?.enabled &&
                !postageData.postage &&
                postageData.courier
              ) && "Free Shipping"}
              {!!(
                !postageLoading &&
                !productDetailData?.enabled &&
                !postageData.postage &&
                postageData.courier
              ) && "Unavailable"}
              {!!(
                !postageLoading &&
                postageData.postage &&
                postageData.courier
              ) && `$${postageData.postage}`}
            </div>
          </div>

          <div className={styles["postage-item"]}>
            <div className={styles["title"]}>ETA: </div>
            <div className={styles["value"]}>
              {!postageLoading && postageData.dTime}
            </div>
          </div>

          {!!(
            productDetailData &&
            [11, 18, 24, 30, 31].includes(productDetailData?.courierID)
          ) && (
            <div className={styles["postage-item"]}>
              <div className={styles["title"]}>
                Ships from and sold by Globaloutlet
              </div>
            </div>
          )}

          {!!(productDetailData?.courierID == 9) && (
            <div className={styles["postage-item"]}>
              <div className={styles["title"]}>No Delivery to PO Box.</div>
            </div>
          )}

          {!!(productDetailData?.courierID == 27) && (
            <div className={styles["postage-item"]}>
              <div className={styles["title"]}>
                This item only delivers to Melbourne metro area.
              </div>
            </div>
          )}

          {!!(
            productDetailData?.preSales && productDetailData?.preSalesDate
          ) && (
            <div className={styles["postage-item"]}>
              <div className={styles["title"]} style={{ color: "#D70B17" }}>
                *This is a PRE-ORDER item, estimated shipping on{" "}
                {dayjs(productDetailData?.preSalesDate).format("DD/MM/YYYY")}
              </div>
            </div>
          )}
        </div>

        {productDetailData?.courierID &&
          [3, 5, 10].includes(productDetailData.courierID) && (
            <div
              className={styles["help-line"]}
              onClick={handleShowCombineModal}
            >
              <Image
                className={styles["shipping-save"]}
                src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/shipping-save.svg`}
                alt="shipping-save"
                width={24}
                height={13}
              />
              <div className={styles["shipping-save-text"]}>
                Combine Shipping & Save
              </div>
              Help
              <Image
                className={styles["icon-help"]}
                src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/icon-help.svg`}
                alt="icon-help"
                width={24}
                height={13}
              />
            </div>
          )}
      </div>

      {paymentMethod && paymentMethod.length ? (
        <div className={styles["payment-method-box"]}>
          <div className={styles["payment-method-title"]}>
            We proudly accept:
          </div>
          <div className={styles["payment-method-list"]}>
            {paymentMethod
              .map((item, index: number) => (
                <Image
                  key={index}
                  className={`${styles["payment-img"]}`}
                  src={item.paymentIcon}
                  alt={item.paymentName}
                  width={57}
                  height={32}
                />
              ))}
          </div>
        </div>
      ) : null}

      {/* 商品状态的对应按钮 */}
      <div className={styles["btn-box"]}>
        <StockStatusBtn></StockStatusBtn>

        {/* 加入心愿单 */}
        <Button
          round
          style={{
            fontWeight: "700",
            marginTop: "15px",
            border: "1px solid #333333",
          }}
          block
          loading={addWishlistLoading}
          loadingText="Add to Wishlist"
          onClick={handleToggleWish}
        >
          <div className={styles["add-wishlist-content"]}>
            <Image
              className={styles["wishlist-icon"]}
              src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/${
                isWish ? "my-wishlist-click" : "my-wishlist"
              }.svg`}
              alt="wishlist"
              width={21}
              height={21}
            />
            Add to Wishlist
          </div>
        </Button>
      </div>

      {productDetailData?.warehouseID === 100002 && (
        <div className={styles["contact-us"]}>
          Wholesale prices are available,
          <span
            className={styles["link"]}
            onClick={() => setShowWholesaleModal(true)}
          >
            contact us
          </span>
          for more discounts on bulk buys.
        </div>
      )}

      {productDetailData?.inStock &&
      productDetailData?.enabled &&
      !productDetailData.preSales ? (
        <div className={styles["float-btn"]}>
          <Button
            round
            type="primary"
            style={{ fontWeight: "700" }}
            block
            className={styles["add-cart"]}
            onClick={(event) => {
              addToCart(
                event,
                {
                  productID: productDetailData.productID,
                  qty: quantity,
                },
                (res: any) => {
                  addToCartCallBack(res);
                }
              );
            }}
          >
            Add to Cart
          </Button>
        </div>
      ) : null}

      {productDetailData.preSales ? (
        <div className={styles["float-btn"]}>
          <Button
            round
            type="primary"
            style={{ fontWeight: "700" }}
            block
            className={styles["pre-sales-btn"]}
            onClick={preOrderNow}
          >
            Pre Sales
          </Button>
        </div>
      ) : null}

      <Dialog
        visible={showAfterPayDialog}
        title=""
        closeable={true}
        showConfirmButton={true}
        showCancelButton={false}
        confirmButtonText="OK"
        onCancel={() => setShowAfterPayDialog(false)}
        onConfirm={() => {
          setShowAfterPayDialog(false);
        }}
      >
        <div
          style={{
            width: "100%",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <Image
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              maxWidth: "100%",
            }}
            src={`${process.env.IMAGE_DOMAIN}/product-detail/afterpay-detail-banner.jpg`}
            alt="afterpay"
            width={750}
            height={962}
          />

          <div
            style={{
              fontSize: "13px",
              color: "#666666",
              paddingTop: "12px",
              wordWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            ① For new Afterpay customers the first payment is due at checkout,
            with the remaining three payments deducted every 2 weeks over 6
            weeks. For existing Afterpay customers the first payment is due in
            14 days (at your next scheduled payment date), with subsequent
            payments every 2 weeks over 8 weeks.
          </div>

          <div
            style={{
              fontSize: "13px",
              color: "#666666",
              paddingTop: "10px",
              wordWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            ② If your scheduled Afterpay payment cannot be processed due to
            insufficient funds, a $10 late fee will be charged immediately. If
            the payment remains unpaid 7 days after the due date, an additional
            $7 fee will apply.
          </div>

          <div
            style={{
              fontSize: "13px",
              color: "#666666",
              paddingTop: "10px",
              wordWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            To apply for Afterpay, you need to be an over 18 years old
            Australian resident with valid Credit / Debit card. Late fees,
            eligibility criteria and T&amp;Cs apply. Afterpay Australia Pty Ltd.
            Australian Credit Licence 527911. For full terms and conditions
            visit&nbsp;
            <Link
              href="https://www.afterpay.com/terms"
              style={{
                color: "#D70B17",
                fontWeight: "bold",
                textDecoration: "underline",
              }}
            >
              www.afterpay.com/terms
            </Link>
            .
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showZippayDialog}
        title=""
        closeable={true}
        showConfirmButton={true}
        showCancelButton={false}
        confirmButtonText="OK"
        onCancel={() => setShowZippayDialog(false)}
        onConfirm={() => {
          setShowZippayDialog(false);
        }}
      >
        <div
          style={{
            width: "100%",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <Image
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              maxWidth: "100%",
            }}
            src={`${process.env.IMAGE_DOMAIN}/product-detail/zippay-explain.jpg`}
            alt="zippay"
            width={488}
            height={595}
          />
        </div>
      </Dialog>

      <Dialog
        visible={showCombineModal}
        title=""
        closeable={true}
        showConfirmButton={true}
        showCancelButton={false}
        confirmButtonText="OK"
        onCancel={() => setShowCombineModal(false)}
        onConfirm={() => {
          setShowCombineModal(false);
        }}
      >
        <div
          style={{
            width: "100%",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              width: "100%",
              padding: "10px 15px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                marginBottom: "15px",
                wordWrap: "break-word",
                wordBreak: "break-word",
                textAlign: "center",
                color: "#D90B17",
              }}
            >
              Save Money on Your Orders, Combine Shipping!
            </div>

            <div
              style={{
                marginTop: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              Why not grab more than one item and save on shipping? To take
              advantage, just add two or more products to your shopping cart.
              <br />(
              <strong>
                Products eligible for combined shipping are marked
              </strong>
              <Image
                style={{
                  display: "inline-block",
                  width: "18px",
                  height: "auto",
                  maxWidth: "100%",
                }}
                src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/shipping-save.svg`}
                alt="shipping-save"
                width={18}
                height={13}
              />
              <span style={{ color: "#D70B17", fontWeight: "bold" }}>
                as Combine Shipping &amp; Save
              </span>
              ).
            </div>
            <div
              style={{
                marginTop: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              After you have finished shopping, proceed to checkout to see how
              much you have saved on shipping.
            </div>
            <p
              style={{
                marginTop: "8px",
                marginBottom: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              Simply finalise payment on your order! Remember, there are greater
              savings to be had by purchasing multiple eligible products!
            </p>
            <div
              style={{
                marginTop: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              <strong>Terms &amp; Conditions:</strong>
              <ul
                style={{
                  marginTop: "5px",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  If your order also contains items that are not eligible for
                  Combined Shipping, you will be charged normal shipping rates
                  on those items.
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  If any discount offer on shipping fees are used on eligible
                  items such as a coupon discount code, the item in question
                  will not be eligible for any further shipping discounts.
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  Actual savings will vary depending on the weight of the item
                  and it&#039;s category.
                </li>
              </ul>
            </div>
            <div
              style={{
                marginTop: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              <strong>FAQ</strong>
              <p
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                }}
              >
                Why am I unable to receive any discounts when purchasing two or
                more products that are eligible for combined shipping?
              </p>
              <p
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                }}
              >
                We have 2 exceptions to the combine shipping system:
              </p>
              <ul
                style={{
                  marginTop: "5px",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  a) Due to minimum charges set by courier companies, there may
                  be no additional shipping discount for individual items
                  weighing less than 0.5kg.
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  b) Items shipped via DHL / UPS or regular mail can only be
                  combined with multiple of the exact same product to receive a
                  combined shipping discount.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showWishLoginModal}
        title=""
        closeable={true}
        showConfirmButton={true}
        showCancelButton={false}
        confirmButtonText="Confirm"
        onCancel={() => setShowWishLoginModal(false)}
        onConfirm={() => {
          setShowWishLoginModal(false);
          handleLogin();
        }}
      >
        <div
          style={{
            width: "100%",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#333333",
              paddingTop: "10px",
              wordWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            Please login to add product to your wishlist!
          </div>
        </div>
      </Dialog>

      <NotifyDialog
        productID={productDetailData.productID}
        showNotify={showNotifyModal}
        onClose={() => setShowNotifyModal(false)}
      ></NotifyDialog>

      <WholesaleDialog
        productID={productDetailData.productID}
        productUrl={`${process.env.API_DOMAIN}${productDetailData?.url}`}
        showWholesale={showWholesaleModal}
        onClose={() => setShowWholesaleModal(false)}
      ></WholesaleDialog>
    </div>
  );
}
